import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { activitiesService } from "@data/services/activitiesService"
import { closeAutoMode } from "@data/redux/actions/liveMode"
import useOwnApiAnswer from "@common/hooks/useOwnApiAnswer"
import useLaunchActivity from "@common/hooks/useLaunchActivity"
import useLiveKitEventSender from "@common/hooks/livekit/useLiveKitEventSender"
import { PlaySoundM } from "@common/components/playSound/PlaySoundM"
import { SnackBarM } from "@common/components/alert/snackbar/SnackBar"
import { notifyBugsnag } from "@common/utils/initBugsnag"

// Handler for the "Refaire la même activité" option of the auto-mode choice
// modal. Reuses the
// same student-scoped endpoint as a tutor's "Redonner l'activité" button:
// POST /api/v2/activities/todo with just material_id, hitting the fast path
// in ActivityController::newTodoActivity (no AI call).
//
// Goes through apiRequest (the standard data flow): the created activity is
// synced into currentUser.todo_activities + ongoing chapters by the existing
// activityTodoAsStudent _SUCCESS reducer case, and request/error status live in
// state.api. The launch + tutor-notification side effects run in the success
// callback.
//
// AutoModeChoiceModal's proposal-choice flow and other screens of the student
// launch the same endpoint: useOwnApiAnswer keeps the side effects to a replay
// that was actually pressed.
const useAutoModeReplaySame = () => {
    const dispatch = useDispatch()
    const launchActivity = useLaunchActivity()
    const { sendLiveKitData } = useLiveKitEventSender()

    const sourceActivity = useSelector(
        (s) => s.liveMode?.autoMode?.sourceActivity
    )
    const currentLiveSession = useSelector((s) =>
        s.data?.currentUser?.object?.live_sessions?.find(
            (x) => x?.id === s.liveMode?.liveSessionId
        )
    )
    const isLoading = useSelector(
        (s) => !!s.api?.activityTodoAsStudent?.request
    )

    const [error, setError] = useState(null)

    // The replay endpoint needs a material_id, so an activity built from an
    // activity_template alone can't be replayed through it. The option stays
    // on screen in that case (disabled + reason) rather than disappearing:
    // an entry that comes and goes from one activity to the next reads as a
    // glitch to the student.
    const canReplay = !!sourceActivity?.material?.id
    const unavailableReason = canReplay
        ? null
        : "Cette activité ne peut pas être relancée"

    const sendReplay = useOwnApiAnswer(
        activitiesService.activityTodoAsStudent,
        (activity) => {
            if (!activity?.id) {
                notifyBugsnag(
                    new Error("[autoMode] replaySame: empty activity response")
                )
                setError("Impossible de relancer cette activité. Réessaie.")
                return
            }
            // Notify tutors so the events panel reflects the choice, same
            // shape as AutoModeChoiceModal but flagged with evaluation:
            // "replay" so the tutor card reads "a choisi de refaire la
            // même activité" instead of the eval-based label.
            const tutorIds = (currentLiveSession?.users ?? [])
                .filter((u) => u?.is_tutor)
                .map((u) => u?.id)
            if (tutorIds.length > 0) {
                sendLiveKitData(
                    "autoModeActivityStarted",
                    { evaluation: "replay", activityId: activity.id },
                    tutorIds
                )
            }
            dispatch(closeAutoMode())
            launchActivity(activity, "replace")
            PlaySoundM.play(require("../../../assets/sounds/falling.mp3"))
            SnackBarM.show({
                text1: "🔁 Activité relancée",
                duration: 3000,
            })
        },
        (err) => {
            // Offline is already surfaced by apiRequest's own retry dialog;
            // don't double up with an inline message.
            if (err?.isOffline) return
            notifyBugsnag(err)
            const status = err?.status
            const backendMsg = err?.message
            const errBits = [status, backendMsg].filter(Boolean).join(" — ")
            setError(
                errBits
                    ? `Impossible de relancer cette activité (${errBits}). Réessaie.`
                    : "Impossible de relancer cette activité. Réessaie."
            )
        }
    )

    const replaySame = () => {
        if (!canReplay || isLoading) return
        setError(null)
        sendReplay({}, { material_id: sourceActivity.material.id })
    }

    return { replaySame, isLoading, error, canReplay, unavailableReason }
}

export default useAutoModeReplaySame
